import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { Check, PlayArrow, Save } from "@material-ui/icons";
import { Autocomplete, Skeleton } from "@material-ui/lab";
import Loading from "components/Loading";
import { RootState } from "config/store";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery, useQuery } from "react-query";
import { useSelector } from "react-redux";
import {
  cancelProduction,
  checkProduction,
  createProduction,
  processProduction,
  processProductionStart,
  Production,
} from "shared/network/production.api";
import { listTools } from "shared/network/tools.api";
import { Tool, Warehouse } from "shared/types";
import { useDebouncedCallback } from "use-debounce";
import RelItemCard from "./RelItemCard";
import { listWarehousePageable } from "shared/network/warehouse.api";
import { useSnackbar } from "notistack";
import ConfirmationButton from "components/ConfirmationButton";

const ItemCard = ({ production, refetch }: { production: Production; refetch: () => void }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const tenant = useSelector((state: RootState) => state.authentication?.selectedRelTenant);
  const form = useForm();
  const { watch, setValue } = form;

  const quantity = watch("quantity");
  const [debouncedQuantity, setDebouncedQuantity] = useState<number>(0);
  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [toolSearch, setToolSearch] = useState<string>("");
  const [warehouseSearch, setWarehouseSearch] = useState("");
  const [processLoading, setProcessLoading] = useState(false);

  const { data, isFetching: isCheckFetching } = useQuery(
    ["checkProduction", production, debouncedQuantity, tenant?.id],
    async () => {
      const { data } = await checkProduction(
        {
          compositeItemId: production?.item?.id as number,
          quantity: debouncedQuantity || 0,
          isCountSubCompositeItems: false,
        },
        tenant?.id,
      );
      return data;
    },
    {
      enabled: !!production,
      onSuccess: data => {
        data?.assemblyItems?.forEach((value, index) => {
          const savedRels = production?.relProductionItemEntries?.filter(
            rel => rel?.item?.id === value?.item?.id,
          );
          savedRels?.forEach((rel, idx) => {
            setValue(`relProductionItems.${index}.warehouses.${idx}.id`, rel?.id || "");
            setValue(`relProductionItems.${index}.warehouses.${idx}.item`, rel?.item || null);
            setValue(
              `relProductionItems.${index}.warehouses.${idx}.warehouse`,
              rel?.warehouse || "",
            );

            setValue(`relProductionItems.${index}.warehouses.${idx}.quantity`, rel?.quantity || "");
          });
        });
      },
    },
  );

  useEffect(() => {
    setValue("quantity", production?.quantity || 1);
    setDebouncedQuantity(production?.quantity || 1);
  }, [production?.quantity, setValue]);

  useEffect(() => {
    setValue("toolEntry", production?.toolEntry || null);
  }, [production?.toolEntry, setValue]);

  // Debounced setter
  const debouncedSetQuantity = useDebouncedCallback((val: number) => {
    setDebouncedQuantity(val);
  }, 400);

  useEffect(() => {
    if (quantity) {
      debouncedSetQuantity(quantity);
    }
  }, [debouncedSetQuantity, quantity]);

  // Infinite pageable tool list
  const {
    data: toolPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery(
    ["toolListForIssueTemplateQuery", tenant.id, toolSearch, production?.id],
    async ({ pageParam = 0 }) => {
      const { data } = await listTools(
        pageParam,
        10,
        tenant.id,
        toolSearch ? `name:${toolSearch}` : ``,
      );
      return data.page;
    },
    {
      getNextPageParam: (lastPage, allPages) =>
        lastPage.number + 1 < lastPage.totalPages ? lastPage.number + 1 : undefined,
    },
  );

  const toolOptions = toolPages?.pages.flatMap(page => page.content) || [];

  const handleToolSearchChange = useDebouncedCallback((value: string) => {
    if (value.length >= 3 || value.length === 0) {
      setToolSearch(value);
    }
  }, 500);

  const subItemList = data?.assemblyItems;

  async function saveProduction() {
    setSaveLoading(true);
    const rels =
      watch("relProductionItems")?.map((rel: any) => rel.warehouses?.map((rel2: any) => rel2)) ||
      [];
    const flattenRel = rels.flat()?.filter((rel: any) => rel?.quantity);

    try {
      await createProduction(
        [
          {
            production: {
              ...production,
              toolEntry: watch(`toolEntry`) || null,
            },
            relProductionItemEntries: flattenRel?.length
              ? flattenRel?.map((value: any) => {
                  return { ...value, productionId: production?.id };
                })
              : [],
          },
        ],
        tenant?.id,
      );
      await refetch();
    } catch {}
    setSaveLoading(false);
  }

  const warehouseListQuery = useQuery(
    ["warehouseListProduction", tenant.id, warehouseSearch],
    async () => {
      const { data } = await listWarehousePageable(
        0,
        20,
        tenant.id,
        warehouseSearch ? `name:${warehouseSearch}` : ``,
      );
      return data.page.content;
    },
  );
  const handleWarehouseSearchChange = useDebouncedCallback((value: string) => {
    if (value.length >= 2 || value.length === 0) {
      setWarehouseSearch(value);
    }
  }, 500);

  const selectedQuantities = form
    .watch(`relProductionItems`)
    ?.map((value: any) =>
      Array.isArray(value.warehouses)
        ? value.warehouses.reduce((sum: number, wh: any) => sum + (Number(wh?.quantity) || 0), 0)
        : 0,
    );

  const allHasQuantity =
    Array.isArray(selectedQuantities) && selectedQuantities.every(qty => qty > 0);
  const [cancelLoading, setCancelLoading] = useState(false);
  return (
    <Card style={{ padding: 16, height: "100%" }}>
      <FormProvider {...form}>
        <form autoComplete="off">
          <Grid container spacing={2}>
            <Grid item xs={12} style={{ display: "flex", justifyContent: "space-between" }}>
              <Typography style={{ fontWeight: "bold", fontFamily: "Montserrat", fontSize: 18 }}>
                {production?.item?.name}
              </Typography>
              {production?.status !== "CANCELLED" ? (
                <ConfirmationButton
                  buttonType="BUTTON"
                  disabled={cancelLoading}
                  size="small"
                  color="primary"
                  style={{ margin: "0 8px" }}
                  onSubmit={async () => {
                    setCancelLoading(true);
                    try {
                      await cancelProduction({ productionId: production.id, tenantId: tenant?.id });
                      enqueueSnackbar(t("Gyártás törölve!"), { variant: "success" });
                      refetch();
                    } catch {
                      enqueueSnackbar(t("Gyártás törlése sikertelen!"), { variant: "error" });
                    }
                    setCancelLoading(false);
                  }}
                  title={"Visszavonás"}
                  body={
                    "Biztosan megszakítja a gyártási folyamatot? Az összetevők visszakerülnek a raktárba."
                  }
                >
                  Gyártás visszavonása
                </ConfirmationButton>
              ) : (
                <Typography>Gyártás visszavonva</Typography>
              )}
            </Grid>
            {production?.status !== "CANCELLED" && (
              <>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="quantity"
                    defaultValue={1}
                    rules={{
                      required: t("validation.required").toString(),
                      min: { value: 1, message: t("validation.minValue", { value: 1 }) },
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        type="number"
                        label={"Mennyiség"}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 1 }}
                        style={{ width: 110 }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        InputProps={{
                          endAdornment:
                            production?.item?.productUnit === "OWN"
                              ? production?.item?.customUnitTypeName
                              : t(`item.unit.${production?.item?.productUnit}`),
                        }}
                        onChange={e => {
                          field.onChange(e);
                          debouncedSetQuantity(Number(e.target.value));
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="toolEntry"
                    defaultValue={production?.toolEntry || null}
                    // rules={{ required: t("validation.required").toString() }}
                    render={({ field }) => (
                      <Autocomplete
                        {...field}
                        onInputChange={(_, newInputValue) => {
                          handleToolSearchChange(newInputValue);
                        }}
                        options={toolOptions}
                        getOptionLabel={(option: Tool) => option?.name || ""}
                        getOptionSelected={option => option.id === field.value?.id}
                        onChange={(_, value) => {
                          field.onChange(value);
                          handleToolSearchChange("");
                        }}
                        ListboxProps={{
                          id: "tool-autocomplete-listbox",
                          style: { maxHeight: 300, overflow: "auto" },
                          onScroll: (event: React.UIEvent<HTMLUListElement>) => {
                            const listboxNode = event.currentTarget;
                            if (
                              listboxNode.scrollTop + listboxNode.clientHeight >=
                              listboxNode.scrollHeight - 10
                            ) {
                              if (hasNextPage && !isFetchingNextPage) {
                                fetchNextPage();
                              }
                            }
                          },
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t("tool.title")}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {(isFetchingNextPage || isFetching) && (
                                    <CircularProgress color="inherit" size={20} />
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
              </>
            )}
          </Grid>
          {production?.status !== "CANCELLED" && (
            <>
              <Box mt={2}>
                <Typography variant="h6" style={{ fontSize: 16 }}>
                  Összetevők:
                </Typography>
                <Grid container spacing={1}>
                  {!subItemList?.length ? (
                    <>Nincs raktáron ez a termék</>
                  ) : (
                    subItemList?.map((assemblyItem, index) => {
                      return (
                        <Grid key={index} item xs={12} sm={6}>
                          {isCheckFetching ? (
                            <Card style={{ padding: 16, minHeight: 120 }}>
                              <Skeleton variant="text" width="60%" height={32} />
                              <Skeleton variant="text" width="40%" height={24} />
                              <Skeleton
                                variant="rect"
                                width="100%"
                                height={40}
                                style={{ marginTop: 8 }}
                              />
                            </Card>
                          ) : (
                            <RelItemCard
                              production={production}
                              assemblyItem={assemblyItem}
                              idx={index}
                              refetch={refetch}
                              saveProduction={saveProduction}
                            />
                          )}
                        </Grid>
                      );
                    })
                  )}
                </Grid>
              </Box>
              <Box
                display="flex"
                justifyContent="flex-end"
                flexWrap="wrap"
                width="100%"
                gridGap={8}
                style={{ marginTop: 8 }}
              >
                <Loading open={saveLoading || processLoading || cancelLoading} />
                <Button
                  disabled={saveLoading}
                  startIcon={<Save />}
                  onClick={async () => saveProduction()}
                >
                  Mentés
                </Button>
                <Button
                  disabled={saveLoading || production?.status !== "CREATED" || !allHasQuantity}
                  startIcon={<PlayArrow />}
                  onClick={async () => {
                    const isValid = await form.trigger();
                    if (isValid) {
                      await saveProduction();
                      setProcessLoading(true);
                      try {
                        await processProductionStart({
                          assemblyItems: production.relProductionItemEntries,
                          productionEntry: production,
                          tenantId: tenant?.id,
                        });
                        refetch();
                        enqueueSnackbar(t("Gyártás megkezdve"), { variant: "success" });
                      } catch {
                        enqueueSnackbar(t("Gyártás megkezdése sikertelen"), {
                          variant: "error",
                        });
                      }
                      setProcessLoading(false);
                    }
                  }}
                >
                  Gyártás megkezdése
                </Button>
                <Button
                  disabled={saveLoading || production?.status !== "IN_PROGRESS" || !allHasQuantity}
                  startIcon={<Check />}
                  onClick={async () => {
                    setWarehouseDialogOpen(true);
                  }}
                >
                  Gyártás befejezése
                </Button>
                <Dialog
                  open={warehouseDialogOpen}
                  onClose={() => setWarehouseDialogOpen(false)}
                  maxWidth="xs"
                  fullWidth
                >
                  <DialogTitle>Célraktár kiválasztása</DialogTitle>
                  <DialogContent>
                    <Typography style={{ marginBottom: 16 }}>
                      Válassz raktárat a gyártáshoz!
                    </Typography>

                    <Controller
                      name="warehouse"
                      defaultValue={null}
                      rules={{ required: t("validation.required").toString() }}
                      render={({ field, fieldState }) => (
                        <Autocomplete
                          {...field}
                          onChange={(_, value) => {
                            field.onChange(value);
                            handleWarehouseSearchChange("");
                          }}
                          onInputChange={(event, newInputValue) => {
                            handleWarehouseSearchChange(newInputValue);
                          }}
                          options={warehouseListQuery.data || []}
                          getOptionLabel={(option: Warehouse) => option.name}
                          getOptionSelected={option => option.id === field.value?.id}
                          renderInput={params => (
                            <TextField
                              {...params}
                              InputLabelProps={{ shrink: true, required: true }}
                              label={t("cargo.formValues.warehouse")}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          )}
                        />
                      )}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setWarehouseDialogOpen(false)} variant="text">
                      Mégse
                    </Button>
                    <Button
                      onClick={async () => {
                        const isValid = await form.trigger();
                        if (isValid) {
                          await saveProduction();
                          setProcessLoading(true);
                          try {
                            await processProduction({
                              assemblyItems: production.relProductionItemEntries,
                              productionEntry: production,
                              toWarehouseId: watch("warehouse")?.id,
                              tenantId: tenant?.id,
                            });
                            refetch();
                            setWarehouseDialogOpen(false);
                            enqueueSnackbar(t("Gyártás befejezése sikeres"), {
                              variant: "success",
                            });
                          } catch {
                            enqueueSnackbar(t("Gyártás befejezése sikertelen"), {
                              variant: "error",
                            });
                          }
                          setProcessLoading(false);
                        }
                      }}
                      color="primary"
                    >
                      OK
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            </>
          )}
        </form>
      </FormProvider>
    </Card>
  );
};

export default ItemCard;
